/**
 * Marketing content configuration
 *
 * All marketing content is configurable via environment variables.
 * Empty values will hide the corresponding UI elements.
 */

export interface PricingPlan {
  name: string
  price: string
  period: string
  description: string
  features: string[]
  cta: string
  badge?: string
  includesFrom?: string
}

export interface Testimonial {
  quote: string
  author: string
  title: string
  initials: string
}

export interface Competitor {
  name: string
  price: string
  caveat: string
}

export interface ComparisonFeature {
  feature: string
  us: boolean
  them: boolean
}

export interface Disclaimers {
  platform: string
  financialEstimate: string
  intelligence: string
  economicAdditionality: string
  dataQuality: string
}

export interface MarketingContent {
  pricing: {
    growth: PricingPlan
  }
  competitors: Competitor[]
  testimonials: Testimonial[]
  stats: {
    userCount: string
    pipelineVelocity: string
    replyRate: string
    roiMultiplier: string
    conversionLift: string
  }
  comparison: ComparisonFeature[]
  faq: {
    securityAnswer: string
  }
  metadata: {
    url: string
    ogImage: string
    twitterHandle: string
  }
  disclaimers: Disclaimers
}

const parseEnvBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined) return defaultValue
  const normalized = value.trim().toLowerCase()
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false
  return defaultValue
}

/**
 * Enterprise plan visibility gate.
 * Defaults to false so enterprise can be hidden until GA readiness.
 */
export const isEnterprisePlanEnabled = parseEnvBoolean(
  import.meta.env.VITE_ENABLE_ENTERPRISE_PLAN,
  false
)

export const marketingContent: MarketingContent = {
  pricing: {
    growth: {
      name: '',
      price: import.meta.env.VITE_PLAN_PRICE || '$149',
      period: '/mo',
      description: 'One flat plan for your organisation, up to 10 people. Managed LLM included. Annual billing 15% off.',
      features: [
        'Daily hot prospects ready to buy with why-now context',
        'AI-personalized email and LinkedIn message drafts in your own voice (you review and send)',
        'Conversation intelligence across Zoom / Meet / Teams',
        'Deal briefings delivered twice daily',
        'Buying committee and relationship context when available',
        'All integrations (CRM, calendar, ads, browser, Slack, Chrome extension)',
        'Signal-triggered cadences — profile visits, post engagement, ad clicks queue the next draft for your review',
      ],
      cta: 'Get started',
    },
  },

  competitors: [
    {
      name: import.meta.env.VITE_COMPETITOR_1_NAME || 'ZoomInfo',
      price: import.meta.env.VITE_COMPETITOR_1_PRICE || '$15,000+/yr',
      caveat: import.meta.env.VITE_COMPETITOR_1_CAVEAT || '+ implementation fees',
    },
    {
      name: import.meta.env.VITE_COMPETITOR_2_NAME || '6sense',
      price: import.meta.env.VITE_COMPETITOR_2_PRICE || '$65,000+/yr',
      caveat: import.meta.env.VITE_COMPETITOR_2_CAVEAT || 'Annual contract',
    },
  ],

  testimonials: [
    {
      quote: import.meta.env.VITE_TESTIMONIAL_QUOTE || '',
      author: import.meta.env.VITE_TESTIMONIAL_AUTHOR || '',
      title: import.meta.env.VITE_TESTIMONIAL_TITLE || '',
      initials: import.meta.env.VITE_TESTIMONIAL_INITIALS || '',
    },
    {
      quote: import.meta.env.VITE_TESTIMONIAL_2_QUOTE || '',
      author: import.meta.env.VITE_TESTIMONIAL_2_AUTHOR || '',
      title: import.meta.env.VITE_TESTIMONIAL_2_TITLE || '',
      initials: import.meta.env.VITE_TESTIMONIAL_2_INITIALS || '',
    },
  ],

  stats: {
    userCount: import.meta.env.VITE_USER_COUNT || '',
    pipelineVelocity: import.meta.env.VITE_PIPELINE_VELOCITY || '',
    replyRate: import.meta.env.VITE_REPLY_RATE || '',
    roiMultiplier: import.meta.env.VITE_ROI_MULTIPLIER || '',
    conversionLift: import.meta.env.VITE_CONVERSION_LIFT || '',
  },

  comparison: [
    { feature: 'Agent-Drafted Outreach', us: true, them: false },
    { feature: 'Human-in-the-Loop Controls', us: true, them: false },
    { feature: 'Network Graph & Warm Intros', us: true, them: false },
    { feature: 'Document Intelligence', us: true, them: false },
    { feature: 'Slack Agent Interface', us: true, them: false },
    { feature: 'AI Cost Management', us: true, them: false },
    { feature: 'Supports SOC 2 & HIPAA Deployments', us: true, them: true },
  ],

  faq: {
    securityAnswer: import.meta.env.VITE_FAQ_SECURITY_ANSWER || 'We treat security as table stakes. Multi-tenant isolation, encryption at rest and in transit, GDPR/CCPA compliant. Your data is never used to train AI models or shared with other customers. Ricald supports deployments into SOC 2 and HIPAA environments for teams with those requirements.',
  },

  metadata: {
    url: import.meta.env.VITE_APP_URL || 'https://ricald.ai',
    ogImage: import.meta.env.VITE_OG_IMAGE || '/og-image.png',
    twitterHandle: import.meta.env.VITE_TWITTER_HANDLE || '',
  },

  disclaimers: {
    platform:
      'All analytics and insights provided by Ricald are for informational purposes only and should not be used as the sole basis for business, financial, or strategic decisions. We recommend independent verification of all data before taking action.',
    financialEstimate:
      'Financial projections and revenue estimates are illustrative calculations based on simplified models. Actual results will vary significantly based on market conditions, sales execution, and other factors beyond this platform\'s visibility.',
    intelligence:
      'Market intelligence and competitive analysis are derived from available data patterns and may not reflect complete market conditions. Confidence scores indicate relative data quality, not prediction accuracy.',
    economicAdditionality:
      'Economic Additionality metrics are estimates derived from your platform activity using configurable model assumptions. These figures represent potential impact ranges, not guaranteed outcomes.',
    dataQuality:
      'Data accuracy depends on the quality and completeness of information provided through integrations and manual entry. Ricald validates data integrity but cannot guarantee accuracy of source data.',
  },
}

/**
 * Helper to check if a testimonial has content
 */
export function hasTestimonial(testimonial: Testimonial): boolean {
  return Boolean(testimonial.quote && testimonial.author)
}

/**
 * Get all testimonials that have content
 */
export function getValidTestimonials(): Testimonial[] {
  return marketingContent.testimonials.filter(hasTestimonial)
}

/**
 * Helper to check if stats are configured
 */
export function hasStats(): boolean {
  return Boolean(marketingContent.stats.userCount)
}

/**
 * Helper to check if performance stats are configured
 */
export function hasPerformanceStats(): boolean {
  return Boolean(
    marketingContent.stats.pipelineVelocity &&
    marketingContent.stats.replyRate &&
    marketingContent.stats.roiMultiplier
  )
}

export default marketingContent
